/**
 * Payment wire ⇄ domain mappers. Generic deep case conversion plus explicit
 * fixups for the fields that don't round-trip cleanly:
 *   - backend `payment_id` (int) → UI `id` (string)
 *   - backend `customer_id` (int) → UI `customerId` (string)
 *   - numeric `amount` arrives as a string from the Decimal column
 *
 * NOTE: confirm field names against the live payment-service contract during
 * real-mode verification and adjust the overrides here only.
 */

import { toCamelDeep, toSnakeDeep } from '@/services/http/caseMap'
import type {
  Payment,
  PaymentStats,
  CreatePaymentInput,
  UpdatePaymentInput,
  OnlinePaymentInput,
  RazorpayCheckoutOrder,
  RazorpayCheckoutProof,
  RazorpaySubscriptionCheckoutOrder,
  RazorpaySubscriptionPaymentResult,
} from '@/types/payment.types'

function toNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number') return value
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value)
    return Number.isNaN(n) ? fallback : n
  }
  return fallback
}

function toStringId(value: unknown): string {
  if (value === null || value === undefined) return ''
  return String(value)
}

export function mapPaymentFromApi(raw: Record<string, unknown>): Payment {
  const camel = toCamelDeep<Record<string, unknown>>(raw)
  return {
    // payment_id → paymentId after the generic pass; surface as `id`.
    id: toStringId(camel.paymentId ?? camel.id),
    tenantId: toStringId(camel.tenantId),
    customerId: toStringId(camel.customerId),
    customerName:
      typeof camel.customerName === 'string' ? camel.customerName : undefined,
    amount: toNumber(camel.amount),
    currency: (camel.currency as Payment['currency']) ?? 'INR',
    paymentMethod: camel.paymentMethod as Payment['paymentMethod'],
    gateway: camel.gateway as Payment['gateway'],
    status: camel.status as Payment['status'],
    transactionId:
      typeof camel.transactionId === 'string' ? camel.transactionId : null,
    providerOrderId:
      typeof camel.providerOrderId === 'string' ? camel.providerOrderId : null,
    providerPaymentId:
      typeof camel.providerPaymentId === 'string' ? camel.providerPaymentId : null,
    providerStatus:
      typeof camel.providerStatus === 'string' ? camel.providerStatus : null,
    description:
      typeof camel.description === 'string' ? camel.description : null,
    paidAt: typeof camel.paidAt === 'string' ? camel.paidAt : null,
    createdAt: toStringId(camel.createdAt),
    updatedAt: toStringId(camel.updatedAt),
  }
}

export function mapPaymentListFromApi(raw: unknown): Payment[] {
  // Accept either a bare array or a { items: [...] } envelope.
  const arr = Array.isArray(raw)
    ? raw
    : ((raw as { items?: unknown[] })?.items ?? [])
  return (arr as Record<string, unknown>[]).map(mapPaymentFromApi)
}

export function mapPaymentStatsFromApi(raw: Record<string, unknown>): PaymentStats {
  const camel = toCamelDeep<Record<string, unknown>>(raw)
  return {
    totalPayments: toNumber(camel.totalPayments),
    totalAmount: toNumber(camel.totalAmount),
    successfulPayments: toNumber(camel.successfulPayments),
    failedPayments: toNumber(camel.failedPayments),
    pendingPayments: toNumber(camel.pendingPayments),
    currency: (camel.currency as PaymentStats['currency']) ?? 'INR',
  }
}

export function mapPaymentCreateToApi(
  input: CreatePaymentInput
): Record<string, unknown> {
  const body = toSnakeDeep<Record<string, unknown>>(input)
  // Customer identifiers are opaque strings and must never be numerically coerced.
  body.customer_id = input.customerId
  return body
}

export function mapOnlinePaymentToApi(
  input: OnlinePaymentInput
): Record<string, unknown> {
  return toSnakeDeep(input)
}

export function mapCheckoutOrderFromApi(
  raw: Record<string, unknown>
): RazorpayCheckoutOrder {
  const camel = toCamelDeep<Record<string, unknown>>(raw)
  return {
    payment: mapPaymentFromApi(camel.payment as Record<string, unknown>),
    orderRecordId: toStringId(camel.orderRecordId),
    providerOrderId: toStringId(camel.providerOrderId),
    keyId: toStringId(camel.keyId),
    amountMinor: toNumber(camel.amountMinor),
    currency: toStringId(camel.currency),
    receipt: toStringId(camel.receipt),
    orderStatus: toStringId(camel.orderStatus),
    expiresAt: typeof camel.expiresAt === 'string' ? camel.expiresAt : null,
  }
}

export function mapCheckoutProofToApi(
  proof: RazorpayCheckoutProof
): Record<string, unknown> {
  return toSnakeDeep(proof)
}

export function mapProviderPaymentResultFromApi(
  raw: Record<string, unknown>
): Payment {
  const camel = toCamelDeep<Record<string, unknown>>(raw)
  const payment = mapPaymentFromApi(camel.payment as Record<string, unknown>)
  return {
    ...payment,
    providerOrderId: toStringId(camel.providerOrderId) || null,
    providerPaymentId: toStringId(camel.providerPaymentId) || null,
    providerStatus: toStringId(camel.providerStatus) || null,
  }
}

export function mapSubscriptionCheckoutOrderFromApi(
  raw: Record<string, unknown>
): RazorpaySubscriptionCheckoutOrder {
  const camel = toCamelDeep<Record<string, unknown>>(raw)
  return {
    orderRecordId: toStringId(camel.orderRecordId),
    tenantId: toStringId(camel.tenantId),
    subscriptionId: toStringId(camel.subscriptionId),
    invoiceId: toStringId(camel.invoiceId),
    providerOrderId: toStringId(camel.providerOrderId),
    keyId: toStringId(camel.keyId),
    amountMinor: toNumber(camel.amountMinor),
    currency: toStringId(camel.currency),
    receipt: toStringId(camel.receipt),
    orderStatus: toStringId(camel.orderStatus),
    expiresAt: typeof camel.expiresAt === 'string' ? camel.expiresAt : null,
  }
}

export function mapSubscriptionPaymentResultFromApi(
  raw: Record<string, unknown>
): RazorpaySubscriptionPaymentResult {
  const camel = toCamelDeep<Record<string, unknown>>(raw)
  return {
    orderRecordId: toStringId(camel.orderRecordId),
    tenantId: toStringId(camel.tenantId),
    subscriptionId: toStringId(camel.subscriptionId),
    invoiceId: toStringId(camel.invoiceId),
    providerOrderId: toStringId(camel.providerOrderId),
    providerPaymentId: toStringId(camel.providerPaymentId),
    providerStatus: toStringId(camel.providerStatus),
    orderStatus: toStringId(camel.orderStatus),
    billingDeliveryStatus: toStringId(camel.billingDeliveryStatus),
  }
}

export function mapPaymentUpdateToApi(
  input: UpdatePaymentInput
): Record<string, unknown> {
  return toSnakeDeep(input)
}
